import { Users, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

const ParticipantRow = ({ p, buddyInvite, currentUserId, onCancelInvite }) => {
  const label = p.completed
    ? 'Completed'
    : p.status === 'pending' ? 'Pending' : 'Accepted';
  const cls = p.completed
    ? 'bg-green-500/20 text-green-400'
    : p.status === 'pending'
      ? 'bg-yellow-500/20 text-yellow-400'
      : 'bg-emerald-500/20 text-emerald-300';
  const iAmHost = buddyInvite.created_by === currentUserId;
  const isCancellable = iAmHost && p.status === 'pending' && p.user_id !== currentUserId;

  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-white text-sm">
        {p.name}{p.user_id === buddyInvite.created_by ? ' · host' : ''}
        {p.user_id === currentUserId ? ' (you)' : ''}
      </span>
      <div className="flex items-center gap-2">
        <span className={`text-xs px-2 py-0.5 rounded ${cls}`} data-testid={`buddy-status-${p.user_id}`}>
          {label}
        </span>
        {isCancellable && (
          <button
            onClick={() => onCancelInvite(p)}
            className="text-muted-foreground hover:text-red-400 text-xs underline"
            data-testid={`cancel-invitee-${p.user_id}`}
            title="Cancel this invite"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export const BuddyWorkoutPanel = ({
  buddyInvite,
  currentUserId,
  isInvitee,
  myStatus,
  respondingInvite,
  onInviteBuddy,
  onAccept,
  onDecline,
  onCancelInvite,
  onGoLive,
}) => {
  if (!buddyInvite) {
    return (
      <Button
        variant="outline"
        className="w-full border-primary/30 text-primary hover:bg-primary/10"
        onClick={onInviteBuddy}
        data-testid="invite-buddy-btn"
      >
        <Users className="h-4 w-4 mr-2" />
        Invite a Buddy
      </Button>
    );
  }

  const acceptedCount = buddyInvite.participants?.filter(p => p.status === 'accepted').length || 0;
  const everyoneFinished = buddyInvite.participants?.every(p => p.status !== 'accepted' || p.completed);

  return (
    <div className="bg-card border border-primary/30 p-4 rounded" data-testid="buddy-workout-panel">
      <div className="flex items-center gap-3 mb-3">
        <Users className="h-5 w-5 text-primary" />
        <span className="font-heading text-white uppercase tracking-wider text-sm">
          Group Workout ({acceptedCount} joined)
        </span>
      </div>
      <div className="space-y-2">
        {buddyInvite.participants?.filter(p => p.status !== 'declined').map(p => (
          <ParticipantRow
            key={p.user_id}
            p={p}
            buddyInvite={buddyInvite}
            currentUserId={currentUserId}
            onCancelInvite={onCancelInvite}
          />
        ))}
      </div>

      {isInvitee && myStatus === 'pending' && (
        <div className="mt-4 pt-3 border-t border-border flex gap-2">
          <Button
            onClick={onAccept}
            disabled={respondingInvite}
            className="flex-1 bg-primary text-black hover:bg-primary/90 font-heading uppercase tracking-wider"
            data-testid="accept-buddy-invite-btn"
          >
            {respondingInvite ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4 mr-2" /> Accept Invite</>}
          </Button>
          <Button
            variant="outline"
            onClick={onDecline}
            disabled={respondingInvite}
            data-testid="decline-buddy-invite-btn"
          >
            Decline
          </Button>
        </div>
      )}

      {everyoneFinished && acceptedCount >= 2 && (
        <div className="mt-3 pt-3 border-t border-border text-center">
          <CheckCircle className="h-6 w-6 text-green-400 mx-auto mb-1" />
          <p className="text-green-400 text-sm font-medium">Everyone crushed it!</p>
        </div>
      )}

      {acceptedCount >= 2 && myStatus === 'accepted' && (
        <Button
          type="button"
          onClick={onGoLive}
          className="w-full mt-3 bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider"
          data-testid="go-live-btn"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-white mr-2 animate-pulse" />
          Go Live · 1-on-1 Video
        </Button>
      )}
    </div>
  );
};
